import {
	type ScoreConfigOverrides,
	currentScoreConfigOverrides,
} from '../../../../packages/@treams/treams-analytics/src/score/services/ScoreConfig';

const STORAGE_KEY = 'treams-launch-score-config';

// Fired on window after save/clear so the score page can rerun the worker.
export const SCORE_CONFIG_CHANGED_EVENT = 'treams-launch-score-config-changed';

// Fired by LaunchTreamsScore when a worker run finishes; detail.error carries
// the worker message when the run failed.
export const SCORE_CONFIG_RECALCULATED_EVENT = 'treams-launch-score-config-recalculated';

type RecalculationDetail = { error?: string } | undefined;

/** Save/clear rejection where the write itself failed: nothing was persisted. */
export class ScoreConfigPersistError extends Error {}

/** Save/clear rejection where the run reported a failure: the config is persisted. */
export class ScoreRecalculationError extends Error {}

// Merges the stored blob over the current defaults per section, so a config
// shape change (new param) never yields a partial overrides object.
export function loadScoreConfigOverrides(): ScoreConfigOverrides | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);

		if (!raw) return null;

		const stored = JSON.parse(raw) as Partial<ScoreConfigOverrides>;
		const defaults = currentScoreConfigOverrides();

		return {
			weights: { ...defaults.weights, ...stored.weights },
			windows: { ...defaults.windows, ...stored.windows },
			engagement: { ...defaults.engagement, ...stored.engagement },
			oneOnOne: { gapBands: stored.oneOnOne?.gapBands ?? defaults.oneOnOne.gapBands },
			targets: { ...defaults.targets, ...stored.targets },
			feedback: { ...defaults.feedback, ...stored.feedback },
			conversations: { ...defaults.conversations, ...stored.conversations },
		};
	} catch {
		return null;
	}
}

const waitForRecalculation = (trigger: () => void): Promise<void> =>
	new Promise((resolve, reject) => {
		const cleanup = () => {
			window.clearTimeout(timeout);
			window.removeEventListener(SCORE_CONFIG_RECALCULATED_EVENT, onDone);
		};

		const onDone = (event: Event) => {
			cleanup();

			const message = (event as CustomEvent<RecalculationDetail>).detail?.error;

			if (message) reject(new ScoreRecalculationError(message));
			else resolve();
		};

		// Safety: never leave Bijwerken spinning forever if the worker stalls.
		// Reject so callers do not treat a stall as a successful recalculation.
		const timeout = window.setTimeout(() => {
			cleanup();
			reject(new Error('Score recalculation timed out'));
		}, 60_000);

		window.addEventListener(SCORE_CONFIG_RECALCULATED_EVENT, onDone);
		trigger();
	});

// Storage can reject the write (quota, blocked storage). Reject with a distinct
// error so callers can tell "nothing saved" apart from "saved but not confirmed".
const persistAndRecalculate = (write: () => void): Promise<void> => {
	try {
		write();
	} catch (err) {
		return Promise.reject(
			new ScoreConfigPersistError(err instanceof Error ? err.message : String(err)),
		);
	}

	return waitForRecalculation(() => {
		window.dispatchEvent(new CustomEvent(SCORE_CONFIG_CHANGED_EVENT));
	});
};

export function saveScoreConfigOverrides(overrides: ScoreConfigOverrides): Promise<void> {
	return persistAndRecalculate(() => localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides)));
}

export function clearScoreConfigOverrides(): Promise<void> {
	return persistAndRecalculate(() => localStorage.removeItem(STORAGE_KEY));
}

export function notifyScoreConfigRecalculated(error?: string): void {
	window.dispatchEvent(
		new CustomEvent<RecalculationDetail>(SCORE_CONFIG_RECALCULATED_EVENT, {
			detail: { error },
		}),
	);
}
